import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  useCancelAlarmMutation,
  useCloseAlarmMutation,
  useGetMobileUnitsQuery,
  usePushAlarmToTechMutation,
  useReassignAlarmMutation,
  useSetAlarmCauseMutation,
} from "../alarmsApi";
import { CAUSE_NEEDING_REMARKS, CAUSE_OPTIONS, CAUSE_STYLE, INCIDENT_LABEL } from "../alarmFormat";

// One modal for every control-room decision on an alarm. Close and Push to Tech
// carry the same cause choice as the stand-alone "Cause" action. The unit already
// picked a cause when reporting (same field) - the operator confirms or changes it.
const COPY = {
  close: { title: "Close alarm", confirm: "Close", variant: "success", done: "Alarm closed" },
  push: { title: "Push to Tech", confirm: "Create complaint", variant: "primary", done: "Pushed to Tech" },
  cause: { title: "Alarm cause", confirm: "Save", variant: "primary", done: "Cause updated" },
  cancel: { title: "Cancel alarm", confirm: "Cancel alarm", variant: "danger", done: "Alarm cancelled" },
  reassign: { title: "Send a different unit", confirm: "Reassign", variant: "primary", done: "Alarm reassigned" },
};

const CausePicker = ({ causes, value, onChange, remarks, onRemarksChange, allowNone, noneLabel }) => (
  <div className="mb-3">
    <label className="form-label">Cause</label>
    <div className="d-flex flex-wrap gap-2 mb-2">
      {allowNone && (
        <button
          type="button"
          className={`btn btn-sm ${value === "" ? "btn-dark" : "btn-outline-secondary"}`}
          onClick={() => onChange("")}
        >
          {noneLabel}
        </button>
      )}
      {causes.map((c) => {
        const active = value === c.code;
        return (
          <button
            key={c.code}
            type="button"
            className="btn btn-sm"
            style={
              active
                ? { ...CAUSE_STYLE[c.code], borderColor: CAUSE_STYLE[c.code]?.background }
                : { border: "1px solid #ced4da", color: "#495057" }
            }
            onClick={() => onChange(active && allowNone ? "" : c.code)}
          >
            {c.label}
          </button>
        );
      })}
    </div>
    {value && (
      <input
        className="form-control form-control-sm"
        maxLength={500}
        value={remarks}
        placeholder={value === CAUSE_NEEDING_REMARKS ? "Describe the cause (required)" : "Why (optional) - e.g. rear window forced"}
        onChange={(e) => onRemarksChange(e.target.value)}
      />
    )}
  </div>
);

const AlarmActionModal = ({ mode, response, onClose }) => {
  const show = !!mode;
  const copy = COPY[mode] || COPY.close;
  const [text, setText] = useState("");
  const [actionTaken, setActionTaken] = useState("");
  const [cause, setCause] = useState("");
  const [causeRemarks, setCauseRemarks] = useState("");
  const [unitGuid, setUnitGuid] = useState("");

  const causes = CAUSE_OPTIONS;
  const { data: units = [] } = useGetMobileUnitsQuery(
    { branchId: response?.branchId },
    { skip: mode !== "reassign" || !response }
  );

  const [closeAlarm, closeState] = useCloseAlarmMutation();
  const [pushToTech, pushState] = usePushAlarmToTechMutation();
  const [setAlarmCause, causeState] = useSetAlarmCauseMutation();
  const [cancelAlarm, cancelState] = useCancelAlarmMutation();
  const [reassignAlarm, reassignState] = useReassignAlarmMutation();
  const saving = [closeState, pushState, causeState, cancelState, reassignState].some((s) => s.isLoading);

  useEffect(() => {
    if (!show || !response) return;
    setActionTaken("");
    setCause(response.alarmCause || "");
    setCauseRemarks(response.causeRemarks || "");
    setUnitGuid("");
    setText(
      mode === "push"
        ? [
            `Alarm ${response.responseNumber} - ${INCIDENT_LABEL[response.incidentType] || response.incidentType}: ${response.incidentDescription}`,
            response.unitRemarks ? `Mobile unit found: ${response.unitRemarks}` : null,
          ]
            .filter(Boolean)
            .join("\n")
            .slice(0, 1000)
        : ""
    );
  }, [show, mode, response]);

  if (!response) return null;
  const recordGuid = response.recordGuid;
  // Only sent when it actually changes, so closing an already-classified alarm does not
  // log the same cause twice. Clearing a cause is the Cause action's job, not Close's.
  const causeChanged =
    cause && (cause !== (response.alarmCause || "") || causeRemarks.trim() !== (response.causeRemarks || ""));
  const causeArgs = causeChanged ? { alarmCause: cause, causeRemarks: causeRemarks.trim() || null } : {};

  const run = async () => {
    try {
      if (mode === "close") await closeAlarm({ recordGuid, actionTaken: actionTaken.trim(), ...causeArgs }).unwrap();
      if (mode === "push")
        await pushToTech({ recordGuid, complaintDescription: text.trim(), actionTaken: actionTaken.trim() || null, ...causeArgs }).unwrap();
      if (mode === "cause")
        await setAlarmCause({ recordGuid, alarmCause: cause || null, remarks: causeRemarks.trim() || null }).unwrap();
      if (mode === "cancel") await cancelAlarm({ recordGuid, reason: text.trim() }).unwrap();
      if (mode === "reassign") await reassignAlarm({ recordGuid, unitRecordGuid: unitGuid }).unwrap();
      toast.success(copy.done);
      onClose();
    } catch (e) {
      toast.error(e?.message || "Could not save");
    }
  };

  const causeRemarksMissing = cause === CAUSE_NEEDING_REMARKS && !causeRemarks.trim();
  const valid =
    !(["close", "push", "cause"].includes(mode) && causeRemarksMissing) &&
    ((mode === "close" && actionTaken.trim()) ||
    (mode === "push" && text.trim()) ||
    mode === "cause" ||
    (mode === "cancel" && text.trim()) ||
    (mode === "reassign" && unitGuid));

  const otherUnits = units.filter((u) => u.recordGuid !== response.unitRecordGuid && u.deviceUserId);

  return (
    <Modal show={show} onHide={saving ? undefined : onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {copy.title} <span className="text-muted fs-6">{response.responseNumber}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {mode === "close" && (
          <>
            <div className="mb-3">
              <label className="form-label">Action taken <span className="text-danger">*</span></label>
              <textarea
                className="form-control"
                rows={3}
                maxLength={1000}
                value={actionTaken}
                placeholder="e.g. Customer informed, police called, site secured"
                onChange={(e) => setActionTaken(e.target.value)}
              />
            </div>
            <CausePicker
              causes={causes}
              value={cause}
              onChange={setCause}
              remarks={causeRemarks}
              onRemarksChange={setCauseRemarks}
              allowNone
              noneLabel={response.alarmCause ? "Keep as is" : "Not set"}
            />
          </>
        )}

        {mode === "push" && (
          <>
            <p className="text-muted small">
              Creates a Technical complaint for {response.customerName}. Technical managers are notified as for any
              logged complaint.
            </p>
            <div className="mb-3">
              <label className="form-label">Complaint for Tech <span className="text-danger">*</span></label>
              <textarea
                className="form-control"
                rows={4}
                maxLength={1000}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Action taken <span className="text-muted">(optional)</span></label>
              <input
                className="form-control"
                maxLength={1000}
                value={actionTaken}
                placeholder="e.g. Customer informed, technician to visit"
                onChange={(e) => setActionTaken(e.target.value)}
              />
            </div>
            <CausePicker
              causes={causes}
              value={cause}
              onChange={setCause}
              remarks={causeRemarks}
              onRemarksChange={setCauseRemarks}
              allowNone
              noneLabel={response.alarmCause ? "Keep as is" : "Not set"}
            />
          </>
        )}

        {mode === "cause" && (
          <>
            <p className="text-muted small">What set this alarm off. The unit chose this when reporting; change it if the control room judges otherwise. Every change is kept in the timeline.</p>
            <CausePicker
              causes={causes}
              value={cause}
              onChange={setCause}
              remarks={causeRemarks}
              onRemarksChange={setCauseRemarks}
              allowNone
              noneLabel="Not set"
            />
          </>
        )}

        {mode === "cancel" && (
          <div className="mb-1">
            <p className="text-muted small">
              {response.unitName} will be told to stand down.
            </p>
            <label className="form-label">Reason <span className="text-danger">*</span></label>
            <textarea
              className="form-control"
              rows={2}
              maxLength={500}
              value={text}
              placeholder="e.g. Customer called - false alarm"
              onChange={(e) => setText(e.target.value)}
            />
          </div>
        )}

        {mode === "reassign" && (
          <div className="mb-1">
            <p className="text-muted small">
              {response.unitName} has not opened this alarm yet. The new unit gets the alert instead.
            </p>
            <label className="form-label">New unit <span className="text-danger">*</span></label>
            <select className="form-select" value={unitGuid} onChange={(e) => setUnitGuid(e.target.value)}>
              <option value="">Select a unit...</option>
              {otherUnits.map((u) => (
                <option key={u.recordGuid} value={u.recordGuid}>
                  {u.unitName}
                  {u.activeResponseNumber ? ` - busy on ${u.activeResponseNumber}` : " - free"}
                </option>
              ))}
            </select>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Back</Button>
        <Button variant={copy.variant} onClick={run} disabled={!valid || saving}>
          {saving ? "Saving..." : copy.confirm}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AlarmActionModal;
